import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MeetingProvider,
  useMeeting,
  useParticipant,
} from "@videosdk.live/react-sdk";
import {
  Mic,
  MicOff,
  Video as VideoIcon,
  VideoOff,
  PhoneOff,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEndCall } from "@/hooks/useVideoCall";
import type { MeetingContext } from "@/services/video-call";

interface VideoCallPageProps {
  meetingContext: MeetingContext;
  onLeave?: () => void;
}

const getInitials = (name: string) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

function ParticipantView({ id }: { id: string }) {
  const { webcamStream, webcamOn, micStream, micOn, isLocal, displayName } =
    useParticipant(id);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (webcamOn && webcamStream && videoRef.current) {
      videoRef.current.srcObject = new MediaStream([webcamStream.track]);
    }
  }, [webcamOn, webcamStream]);

  useEffect(() => {
    if (micOn && micStream && audioRef.current) {
      audioRef.current.srcObject = new MediaStream([micStream.track]);
    }
  }, [micOn, micStream]);

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden">
      {webcamOn ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="object-cover w-full h-full"
        />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-full text-white bg-gray-800">
          <div className="w-20 h-20 mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl font-bold">
            {getInitials(displayName || "User")}
          </div>
          <p className="text-gray-300">Camera is off</p>
        </div>
      )}
      {!isLocal && <audio ref={audioRef} autoPlay />}

      {/* Participant name overlay */}
      <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
        {isLocal ? "You" : displayName}
      </div>

      {/* Mic status indicator */}
      {!micOn && (
        <div className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full">
          <MicOff size={16} />
        </div>
      )}
    </div>
  );
}

function AudioParticipantView({ participantId }: { participantId: string }) {
  const { isLocal, displayName, micOn, micStream } =
    useParticipant(participantId);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (micOn && micStream && audioRef.current) {
      audioRef.current.srcObject = new MediaStream([micStream.track]);
    }
  }, [micOn, micStream]);

  return (
    <div className="flex flex-col items-center space-y-4 relative">
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-4xl font-bold">
        {getInitials(isLocal ? "You" : displayName || "Participant")}
      </div>
      <div className="text-center">
        <h3 className="text-white text-xl font-semibold">
          {isLocal ? "You" : displayName || "Participant"}
        </h3>
        <p className="text-gray-400">Voice Call</p>
      </div>
      {!isLocal && <audio ref={audioRef} autoPlay />}

      {/* Mic status indicator */}
      {!micOn && (
        <div className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full">
          <MicOff size={16} />
        </div>
      )}
    </div>
  );
}

function CallUI({
  meetingContext,
  onLeave,
}: {
  meetingContext: MeetingContext;
  onLeave?: () => void;
}) {
  const [isEnding, setIsEnding] = useState(false);

  const endCall = useEndCall({
    onSuccess: () => {
      setIsEnding(false);
      onLeave?.();
    },
    onError: () => {
      setIsEnding(false);
    },
  });

  const recordingStartedRef = useRef(false);

  const {
    join: joinMeeting,
    leave,
    toggleMic,
    toggleWebcam,
    startRecording,
    participants,
    localParticipant,
    presenterId,
  } = useMeeting({
    onMeetingJoined: () => {
      // Start cloud recording automatically when meeting is joined (fallback if student didn't start it)
      if (!recordingStartedRef.current) {
        recordingStartedRef.current = true;
        try {
          startRecording(undefined, undefined, {
            layout: { type: "GRID", priority: "PIN", gridSize: 4 },
            theme: "DEFAULT",
            mode: "video-and-audio",
            quality: "high",
            orientation: "landscape",
          });
          console.log("Recording started automatically");
        } catch (error) {
          console.warn("Failed to start recording (may already be active):", error);
        }
      }
    },
    onRecordingStarted: () => {
      console.log("Recording has started");
    },
    onRecordingStopped: () => {
      console.log("Recording has stopped");
    },
    onError: (error: unknown) => {
      console.error("Meeting error:", error);
    },
  });

  const { micOn: isMicOn, webcamOn: isWebcamOn } = useParticipant(
    localParticipant?.id || ""
  );

  const [isTogglingMic, setIsTogglingMic] = useState(false);
  const [isTogglingWebcam, setIsTogglingWebcam] = useState(false);
  const hasJoinedRef = useRef(false);
  const isLeavingRef = useRef(false);

  useEffect(() => {
    if (!hasJoinedRef.current && !isLeavingRef.current) {
      setTimeout(() => {
        joinMeeting();
      }, 0);
      hasJoinedRef.current = true;
    }
  }, [meetingContext.meetingId]);

  const handleMicToggle = async () => {
    if (isTogglingMic) return;

    setIsTogglingMic(true);
    try {
      await toggleMic();
    } catch (error: unknown) {
      if (
        (error instanceof Error &&
          error.message?.includes("Permission denied")) ||
        (error instanceof Error && error.message?.includes("NotAllowedError"))
      ) {
        alert(
          "Please allow microphone access to unmute. You may need to refresh the page and grant permissions."
        );
      }
    } finally {
      setIsTogglingMic(false);
    }
  };

  const handleWebcamToggle = async () => {
    if (isTogglingWebcam) return;

    // Don't allow video toggle for audio calls
    if (meetingContext.callType === "audio") {
      return;
    }

    setIsTogglingWebcam(true);
    try {
      await toggleWebcam();

      // Additional cleanup to completely stop video stream when turning off
      if (isWebcamOn) {
        // Stop all video tracks to turn off camera light
        setTimeout(() => {
          try {
            const videoElement = document.querySelector(
              "video[autoplay][muted]"
            ) as HTMLVideoElement;
            if (videoElement && videoElement.srcObject) {
              const stream = videoElement.srcObject as MediaStream;
              const videoTracks = stream.getVideoTracks();
              videoTracks.forEach((track) => {
                track.stop();
                stream.removeTrack(track);
              });
            }
          } catch (cleanupError) {
            // Video cleanup completed
          }
        }, 100);
      }
    } catch (error: unknown) {
      if (
        (error instanceof Error &&
          error.message?.includes("Permission denied")) ||
        (error instanceof Error && error.message?.includes("NotAllowedError"))
      ) {
        alert(
          "Please allow camera access to turn on video. You may need to refresh the page and grant permissions."
        );
      }
    } finally {
      setIsTogglingWebcam(false);
    }
  };

  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const duration = useMemo(() => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(
        secs
      ).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  }, [seconds]);

  const handleEndCall = async () => {
    if (isEnding || isLeavingRef.current) return;

    setIsEnding(true);
    isLeavingRef.current = true;

    try {
      await endCall.mutateAsync({
        callId: meetingContext.callId,
        reason: "ended_by_counselor",
      });
      hasJoinedRef.current = false;
      await leave();
      // Manually call onLeave since we removed the onMeetingLeft callback
      onLeave?.();
    } catch (error: unknown) {
      // Still try to leave the meeting even if API call fails
      hasJoinedRef.current = false;
      await leave();
      // Manually call onLeave since we removed the onMeetingLeft callback
      onLeave?.();
    }
  };


  const participantList = Array.from(participants.keys());
  const participant = meetingContext.participant;
  const isAudioCall = meetingContext.callType === "audio";

  useEffect(() => {
    return () => {
      handleEndCall();
    };
  }, []);

  useEffect(() => {
    const handlePageRefresh = (_e: BeforeUnloadEvent) => {
      // This runs when page is about to refresh/close
      handleEndCall();

      // Set flags immediately
      isLeavingRef.current = true;

      // Important: You can't use async operations reliably here
      // Use synchronous alternatives or navigator.sendBeacon

      // Option 1: Use sendBeacon for API calls (most reliable)
      if (navigator.sendBeacon && meetingContext.callId) {
        const formData = new FormData();
        formData.append("callId", meetingContext.callId);
        formData.append("reason", "page_refresh");
        navigator.sendBeacon("/api/calls/end", formData);
      }

      // Option 2: Try synchronous localStorage to save state
      localStorage.setItem(
        "pendingCallCleanup",
        JSON.stringify({
          callId: meetingContext.callId,
          timestamp: Date.now(),
        })
      );

      // Option 3: Try to leave meeting synchronously
      try {
        // Note: This might not complete before page unloads
        leave();
      } catch (error: unknown) {
        // Failed to leave meeting on refresh
      }
    };

    // Add the event listener
    window.addEventListener("beforeunload", handlePageRefresh);

    // Normal cleanup for component unmount
    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);

      // Your existing cleanup logic for normal unmounts
      if (!isLeavingRef.current) {
        handleEndCall();
      }
    };
  }, [meetingContext.callId]);


  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col z-50">
      {/* Header */}
      <header className="flex items-center justify-between p-4 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
            {participant?.avatar ? (
              <img
                src={participant.avatar}
                alt={participant.fullName}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(participant?.fullName || "Student")
            )}
          </div>
          <div>
            <h3 className="text-white font-semibold">
              {participant?.fullName ||
                (isAudioCall ? "Voice Call" : "Video Call")}
            </h3>
            <p className="text-gray-400 text-sm">
              {isAudioCall
                ? "Voice Counseling Session"
                : "Video Counseling Session"}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-white">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">{duration}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLeave}
            className="text-gray-400 hover:text-white"
          >
            <X size={18} />
          </Button>
        </div>
      </header>

      {/* Video Grid / Audio Call UI */}
      <main className="flex-1 relative p-4">
        {isAudioCall ? (
          /* Audio Call UI - Show avatars instead of video */
          <div className="w-full h-full flex items-center justify-center">
            <div className="flex flex-col items-center space-y-8">
              {/* Show all participants in audio call */}
              <div className="flex space-x-8">
                {participantList.map((participantId) => (
                  <AudioParticipantView
                    key={participantId}
                    participantId={participantId}
                  />
                ))}
              </div>

              {/* Audio visualizer placeholder */}
              <div className="flex space-x-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-8 bg-blue-500 rounded-full animate-pulse"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Video Call UI - Show video participants */
          <>
            <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 gap-4">
              {participantList.map((participantId) => (
                <div key={participantId} className="relative">
                  <ParticipantView id={participantId} />
                </div>
              ))}
            </div>

            {/* Local participant (self view) */}
            {localParticipant && (
              <div className="absolute bottom-4 right-4 w-48 h-36 border-2 border-white rounded-lg overflow-hidden shadow-lg">
                <ParticipantView id={localParticipant.id} />
              </div>
            )}
          </>
        )}

        {/* Screen share indicator */}
        {presenterId && !isAudioCall && (
          <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
            Screen sharing active
          </div>
        )}
      </main>

      {/* Controls */}
      <footer className="flex items-center justify-center space-x-4 p-6 bg-gray-800 border-t border-gray-700">
        <Button
          variant={isMicOn ? "secondary" : "destructive"}
          size="lg"
          onClick={handleMicToggle}
          disabled={isTogglingMic}
          className="rounded-full w-12 h-12 p-0"
        >
          {isTogglingMic ? (
            <Loader2 size={20} className="animate-spin" />
          ) : isMicOn ? (
            <Mic size={20} />
          ) : (
            <MicOff size={20} />
          )}
        </Button>

        {/* Hide video button for audio calls */}
        {!isAudioCall && (
          <Button
            variant={isWebcamOn ? "secondary" : "destructive"}
            size="lg"
            onClick={handleWebcamToggle}
            disabled={isTogglingWebcam}
            className="rounded-full w-12 h-12 p-0"
          >
            {isTogglingWebcam ? (
              <Loader2 size={20} className="animate-spin" />
            ) : isWebcamOn ? (
              <VideoIcon size={20} />
            ) : (
              <VideoOff size={20} />
            )}
          </Button>
        )}


        <Button
          variant="destructive"
          size="lg"
          onClick={handleEndCall}
          disabled={isEnding}
          className="rounded-full w-14 h-14 p-0 bg-red-600 hover:bg-red-700"
        >
          <PhoneOff size={24} />
        </Button>
      </footer>
    </div>
  );
}

export const VideoCallPage: React.FC<VideoCallPageProps> = ({
  meetingContext,
  onLeave,
}) => {
  const isAudioCall = meetingContext.callType === "audio";

  return (
    <MeetingProvider
      config={{
        meetingId: meetingContext.roomId,
        micEnabled: true,
        webcamEnabled: !isAudioCall, // Disable webcam for audio calls
        name: "Counselor",
        debugMode: true,
      }}
      token={meetingContext.token}
    >
      <CallUI meetingContext={meetingContext} onLeave={onLeave} />
    </MeetingProvider>
  );
};
